/**
 * College names, slugs, and logos for class breakdowns.
 * Static directory first; season JSON fills schools the directory does not list.
 */

import collegeDirectory from '../data/collegeData.json';
import { conferenceData } from '../data/conferenceData';
import { getCollegeSlug } from './collegeUtils';
import { getConferenceIdForTeam } from './conferenceUtils';

export interface CollegeMeta {
  id: string;
  slug: string;
  name: string;
  logo: string;
  conferenceId: string | null;
}

export interface ConferenceMeta {
  id: string;
  slug: string;
  name: string;
  logo: string | null;
}

export const OTHER_CONFERENCE_ID = 'other';

type DirectoryCollege = { slug?: string; displayName?: string };
type SeasonCollege = {
  slug?: string;
  displayName?: string;
  logos?: Array<{ href?: string }>;
};

const directory = (collegeDirectory as { colleges: Record<string, DirectoryCollege> }).colleges || {};

export const espnCollegeLogo = (collegeId: string): string =>
  `https://a.espncdn.com/i/teamlogos/ncaa/500/${collegeId}.png`;

const slugFromName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const resolveCollegeMeta = (
  collegeId: string,
  seasonColleges?: Record<string, SeasonCollege> | null
): CollegeMeta => {
  const season = seasonColleges?.[collegeId];
  const listed = directory[collegeId];
  const name = season?.displayName || listed?.displayName || `College ${collegeId}`;
  const slug = season?.slug || listed?.slug || getCollegeSlug(season) || slugFromName(name) || collegeId;
  const logo = season?.logos?.[0]?.href || espnCollegeLogo(collegeId);
  return {
    id: collegeId,
    slug,
    name,
    logo,
    conferenceId: getConferenceIdForTeam(collegeId),
  };
};

export const resolveConferenceMeta = (conferenceId: string | null): ConferenceMeta => {
  if (!conferenceId) {
    return {
      id: OTHER_CONFERENCE_ID,
      slug: OTHER_CONFERENCE_ID,
      name: 'Independent / other',
      logo: null,
    };
  }
  const conference = (conferenceData.conferences as Record<
    string,
    { abbreviation?: string; name?: string; shortName?: string; logo?: string }
  >)[conferenceId];
  if (!conference) {
    return {
      id: OTHER_CONFERENCE_ID,
      slug: OTHER_CONFERENCE_ID,
      name: 'Independent / other',
      logo: null,
    };
  }
  return {
    id: conferenceId,
    slug: (conference.abbreviation || conferenceId).toLowerCase(),
    name: conference.shortName || conference.name || conferenceId,
    logo: conference.logo || null,
  };
};

export const collegeNameForSlug = (slug: string): string | null => {
  const wanted = slug.toLowerCase();
  const listed = Object.keys(directory);
  for (let i = 0; i < listed.length; i++) {
    const college = directory[listed[i]];
    if (college?.slug === wanted) return college.displayName || college.slug || null;
  }
  return null;
};

/** Directory id for a known slug (e.g. ohio-state-buckeyes → 194). */
export const collegeIdForDirectorySlug = (slug: string): string | null => {
  const wanted = slug.toLowerCase();
  const listed = Object.keys(directory);
  for (let i = 0; i < listed.length; i++) {
    if (directory[listed[i]]?.slug === wanted) return listed[i];
  }
  return null;
};

export const collegeIdForSlug = (
  slug: string,
  ids: Iterable<string>,
  seasonColleges?: Record<string, SeasonCollege> | null
): string | null => {
  const wanted = slug.toLowerCase();
  const collegeIds = Array.from(ids);
  for (let i = 0; i < collegeIds.length; i++) {
    const id = collegeIds[i];
    if (resolveCollegeMeta(id, seasonColleges).slug === wanted) return id;
  }
  return null;
};
